import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, AlertCircle, ShieldCheck, Clock, Bell, Users, ArrowRight, FileText, Mail } from "lucide-react";
import api from "../../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function scoreRing({ score = 0, color = "green" }) {
  const ringColors = {
    green: { bg: "#E6F0EB", track: "#D6E0D9", arc: "#34745C", text: "#34745C" },
    yellow: { bg: "#FDF3E1", track: "#F0E1C0", arc: "#C48231", text: "#C48231" },
    red: { bg: "#FCEAE8", track: "#F0D9D5", arc: "#B54D40", text: "#B54D40" },
  };
  const c = ringColors[color] || ringColors.green;
  const pct = Math.max(0, Math.min(100, score));
  const dash = `${pct}, 100`;
  return (
    <div className="relative w-32 h-32" data-testid="compliance-score-ring">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <circle cx="18" cy="18" r="15.915" fill="none" stroke={c.track} strokeWidth="2.6" />
        <circle cx="18" cy="18" r="15.915" fill="none" stroke={c.arc} strokeWidth="2.6"
          strokeDasharray={dash} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-bold" style={{ color: c.text }}>{score}%</span>
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#57605A]">Compliant</span>
      </div>
    </div>
  );
}

function SeverityBadge({ status }) {
  const map = {
    Missing:        { cls: "bg-[#FCEAE8] text-[#B54D40] border-[#B54D40]/30", icon: AlertCircle },
    Expired:        { cls: "bg-[#FCEAE8] text-[#B54D40] border-[#B54D40]/30", icon: AlertCircle },
    "Expiring Soon":{ cls: "bg-[#FDF3E1] text-[#C48231] border-[#C48231]/30", icon: AlertTriangle },
  };
  const m = map[status] || map["Expiring Soon"];
  const I = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 ${m.cls} border px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap`}>
      <I className="w-3 h-3" strokeWidth={2} /> {status}
    </span>
  );
}

export default function WorkforceComplianceDashboard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/compliance/dashboard").then((r) => {
      if (mounted) {
        setData(r.data);
        setLoading(false);
      }
    }).catch(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-[#57605A] py-12 text-center">Unable to load compliance data.</p>;
  }

  const { kpis, employees, action_center } = data;

  return (
    <div data-testid="workforce-dashboard" className="space-y-10">
      {/* Heading */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Workforce Compliance</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-[#1C211F]">
            Are we compliant <span className="font-bold text-[#2E5C50]">today?</span>
          </h1>
          <p className="mt-2 text-sm text-[#57605A]">Welcome back, {user?.name?.split(" ")[0] || "Agency Owner"} — here's the live picture.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#57605A]">
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
          As of {new Date(data.as_of).toLocaleString()}
        </div>
      </div>

      {/* Hero compliance score + 4 KPI cards */}
      <div className="grid lg:grid-cols-5 gap-4">
        <div data-testid="kpi-agency-score"
             className="lg:col-span-1 bg-white border border-[#E8E6E1] rounded-xl p-6 flex flex-col items-center gap-3">
          {scoreRing({ score: kpis.agency_score, color: kpis.agency_color })}
          <p className="text-sm font-semibold text-[#1C211F]">Agency Compliance Score</p>
          <p className="text-xs text-[#57605A]">Target {kpis.agency_target}%</p>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="kpi-expiring-30"
                   icon={Clock} accent="amber"
                   label="Expiring ≤30 days" value={kpis.expiring_30} />
          <KpiCard testId="kpi-expired"
                   icon={AlertCircle} accent="red"
                   label="Expired credentials" value={kpis.expired} />
          <KpiCard testId="kpi-pending-responses"
                   icon={Users} accent="neutral"
                   label="Pending employee responses" value={kpis.pending_responses}
                   note="Activates in Phase 3" />
          <KpiCard testId="kpi-notifications-sent"
                   icon={Bell} accent="neutral"
                   label="Notifications sent (mo.)" value={kpis.notifications_sent_this_month}
                   note="Activates in Phase 3" />
        </div>
      </div>

      {/* Compliance Action Center */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl text-[#1C211F]">Compliance Action Center</h2>
            <p className="text-sm text-[#57605A] mt-0.5">
              {data.action_center_total === 0
                ? "No urgent actions. You're all caught up."
                : `${data.action_center_total} item${data.action_center_total === 1 ? "" : "s"} need attention — ranked by severity.`}
            </p>
          </div>
          <Link to="/credentials" data-testid="action-view-all"
                className="inline-flex items-center gap-1 text-sm text-[#2E5C50] hover:text-[#22463D] font-medium">
            View all credentials <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-xl overflow-hidden">
          {action_center.length === 0 ? (
            <div data-testid="action-empty" className="flex flex-col items-center justify-center py-16">
              <ShieldCheck className="w-10 h-10 text-[#34745C] mb-3" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-[#1C211F]">All clear</p>
              <p className="mt-1 text-sm text-[#57605A]">No expiring or missing required credentials.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F9F7F4] text-[#57605A] text-xs uppercase tracking-[0.12em]">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Employee</th>
                  <th className="text-left py-3 px-4 font-semibold">Credential</th>
                  <th className="text-left py-3 px-4 font-semibold">Expires</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Recommended action</th>
                </tr>
              </thead>
              <tbody>
                {action_center.map((a, i) => (
                  <tr key={`${a.employee_id}-${a.credential_type}-${i}`} data-testid={`action-row-${i}`}
                      className="border-t border-[#E8E6E1] hover:bg-[#F9F7F4]/50">
                    <td className="py-3 px-4 text-[#1C211F] font-medium">{a.employee_name}</td>
                    <td className="py-3 px-4 text-[#1C211F]">
                      <span className="inline-flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#2E5C50]" strokeWidth={1.5} />
                        {a.credential_label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#57605A]">
                      {a.expiration_date ? (
                        <span>{fmtDate(a.expiration_date)}{a.days_to_expiry != null && (
                          <span className="ml-1.5 text-xs text-[#57605A]">
                            ({a.days_to_expiry < 0 ? `${Math.abs(a.days_to_expiry)}d ago` : `${a.days_to_expiry}d`})
                          </span>
                        )}</span>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4"><SeverityBadge status={a.status} /></td>
                    <td className="py-3 px-4 text-[#57605A]">{a.recommended_action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Employee Roster Scores */}
      {employees.length > 0 && (
        <section>
          <h2 className="font-heading text-xl text-[#1C211F]">Employee Compliance</h2>
          <p className="text-sm text-[#57605A] mt-0.5">Individual score per employee.</p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {employees.slice(0, 12).map((e) => (
              <div key={e.id} data-testid={`employee-card-${e.id}`}
                   className="bg-white border border-[#E8E6E1] rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#1C211F]">{e.name}</p>
                  <p className="text-xs text-[#57605A] mt-0.5">
                    {e.breakdown.active} active · {e.breakdown.expiring_soon} expiring · {e.breakdown.expired} expired · {e.breakdown.missing} missing
                  </p>
                </div>
                <span className={`font-heading text-lg font-bold ${
                  e.color === "green" ? "text-[#34745C]"
                  : e.color === "yellow" ? "text-[#C48231]"
                  : "text-[#B54D40]"
                }`}>{e.score}%</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function KpiCard({ testId, icon: Icon, accent, label, value, note }) {
  const accentMap = {
    amber:   "border-[#C48231]/30 bg-[#FDF3E1]",
    red:     "border-[#B54D40]/30 bg-[#FCEAE8]",
    neutral: "border-[#E8E6E1] bg-white",
  };
  const iconColor = accent === "amber" ? "text-[#C48231]" : accent === "red" ? "text-[#B54D40]" : "text-[#57605A]";
  return (
    <div data-testid={testId} className={`border ${accentMap[accent]} rounded-xl p-5 flex flex-col`}>
      <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />
      <p className="mt-3 text-xs uppercase tracking-[0.16em] font-semibold text-[#57605A]">{label}</p>
      <p className="mt-1 font-heading text-3xl font-bold text-[#1C211F]">{value}</p>
      {note && <p className="mt-1.5 text-[10px] text-[#57605A]">{note}</p>}
    </div>
  );
}
